import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from './button';

interface DropZoneProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onDrop'> {
  onDrop?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  dragActiveClassName?: string;
  accept?: string;
  preview?: string;
  setPreview?: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const DropZone = React.forwardRef<HTMLDivElement, DropZoneProps>(
  (
    {
      className,
      dragActiveClassName = 'border-primary',
      accept,
      onDrop,
      preview,
      setPreview,
      children,
      ...props
    },
    ref
  ) => {
    const [isDragging, setIsDragging] = React.useState(false);

    const inputRef = React.useRef<HTMLInputElement | null>(null);

    const handleDragOver = React.useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
      },
      []
    );

    const handleDragLeave = React.useCallback(() => {
      setIsDragging(false);
    }, []);

    const handleDrop = React.useCallback(
      (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);

        const files = event.dataTransfer.files
          ? Array.from(event.dataTransfer.files)
          : [];

        if (inputRef.current) {
          const dataTransfer = new DataTransfer();
          files.forEach((file) => dataTransfer.items.add(file));

          inputRef.current.files = dataTransfer.files;

          if (onDrop) {
            const changeEvent = new Event('change', {
              bubbles: true,
              cancelable: true,
            }) as Event & { target: HTMLInputElement };

            inputRef.current.dispatchEvent(changeEvent);
          }
        }
      },
      [onDrop]
    );

    const handleFileChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        if (onDrop) {
          onDrop(event);
        }
      },
      [onDrop]
    );

    if (preview && setPreview) {
      return (
        <div className="border-2 border-dashed rounded-lg text-center relative group overflow-hidden">
          <img
            src={preview}
            alt="preview"
            className="h-[228px] w-full object-contain"
          />
          <Button
            type="button"
            onClick={() => {
              setPreview('');
            }}
            className="absolute bottom-2 left-1/2 -translate-x-1/2 transition-opacity duration-200 ease-out-strong opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:focus-visible:opacity-100"
            variant="secondary"
          >
            Close Preview
          </Button>
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center relative',
          isDragging ? dragActiveClassName : 'border-input',
          className
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        {...props}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          onChange={handleFileChange}
        />
        {children}
      </div>
    );
  }
);

DropZone.displayName = 'DropZone';

export { DropZone };
